import { Modal } from "@dropbox/dig-components/modal";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { MODAL_ANIMATIONS } from "@mirage/mosaics/Layout/constants";
import { tagged } from "@mirage/service-logging";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { DocumentDetailsPanel } from "./DocumentDetailsPanel";
import { DocumentPreviewArea } from "./DocumentPreviewArea";
import { DocumentPreviewHeader } from "./DocumentPreviewHeader";
import styles from "./DocumentPreviewModal.module.css";
import { SummaryFirstDocumentPreviewModal } from "./SummaryFirstDocumentPreviewModal";
import { usePreviewModalAnalytics } from "./hooks/usePreviewModalAnalytics";

import type { DocumentFollowUpSuggestion } from "@mirage/mosaics/QuerySuggestions/useDocumentFollowUpSuggestions";
import type { ResultActionConfig } from "@mirage/search/SearchResults/ResultRow/types";
import type { SearchResult } from "@mirage/service-dbx-api";

const logger = tagged("document-preview-modal");

function shouldUseDropboxViewer(result: SearchResult): boolean {
  if (!result.url) return false;

  try {
    const url = new URL(result.url);
    return (
      url.hostname === "dropbox.com" ||
      url.hostname.startsWith("www.dropbox.com")
    );
  } catch (error) {
    logger.warn("Invalid URL provided for Dropbox check:", error);
    return false;
  }
}

// could add back the isPreviewable function if logic becomes more complex

type DocumentPreviewModalProps = {
  open: boolean;
  onClose: () => void;
  result: SearchResult;
  resultActionConfig: ResultActionConfig;
  onSuggestionClick: (suggestion: DocumentFollowUpSuggestion) => void;
};

export function DocumentPreviewModal({
  open,
  onClose,
  result,
  resultActionConfig,
  onSuggestionClick,
}: DocumentPreviewModalProps) {
  const { t } = useTranslation();
  const { searchSessionManager } = useMirageAnalyticsContext();
  const searchSessionId = searchSessionManager.getSessionIdOrUndefined();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentPage, _] = useState(1);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [previewWidth, setPreviewWidth] = useState(60);
  const [isDragging, setIsDragging] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const containerRef = useRef<HTMLDivElement>(null);
  const previewModalUuid = useRef(uuidv4());

  const {
    logZoomInteraction,
    logPanelResize,
    logNavigation,
    logOpenInProvider,
    logCopyLink,
    logAddToStack,
    logAskFollowUpQuestion,
    logFileDownloadComplete,
  } = usePreviewModalAnalytics({
    isOpen: open,
    searchSessionId,
    connector: result.connectorInfo.connectorId,
    resultUuid: result.uuid,
    dashActionSurface: "serp",
    previewModalUuid: previewModalUuid.current,
  });

  const handleZoomIn = useCallback(() => {
    const newZoom = Math.min(zoomLevel + 25, 200);
    setZoomLevel(newZoom);
    logZoomInteraction(newZoom, true);
  }, [zoomLevel, logZoomInteraction]);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoomLevel - 25, 50);
    setZoomLevel(newZoom);
    logZoomInteraction(newZoom, false);
  }, [zoomLevel, logZoomInteraction]);

  const handleZoomChange = useCallback(
    (level: number) => {
      const clamped = Math.min(Math.max(level, 50), 200);
      setZoomLevel(clamped);
      logZoomInteraction(clamped, clamped > zoomLevel);
    },
    [zoomLevel, logZoomInteraction],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const newWidth =
          ((moveEvent.clientX - containerRect.left) / containerRect.width) *
          100;

        const constrainedWidth = Math.min(Math.max(newWidth, 45), 80);
        setPreviewWidth(constrainedWidth);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        logPanelResize(previewWidth);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [previewWidth, logPanelResize],
  );

  const handleSuggestionClick = useCallback(
    (suggestion: DocumentFollowUpSuggestion) => {
      logAskFollowUpQuestion();
      onSuggestionClick(suggestion);
    },
    [logAskFollowUpQuestion, onSuggestionClick],
  );

  const handleOpenInProvider = useCallback(() => {
    logOpenInProvider();
    const openLinkAction = resultActionConfig?.displayActions?.find(
      (action) => action.key === "open_link",
    );
    openLinkAction?.onClick?.("click");
  }, [logOpenInProvider, resultActionConfig]);

  const handleCopyLink = useCallback(() => {
    logCopyLink();
    const copyAction = resultActionConfig?.displayActions?.find(
      (action) => action.key === "copy",
    );
    copyAction?.onClick?.("click");
  }, [logCopyLink, resultActionConfig]);

  const handleAddToStack = useCallback(() => {
    logAddToStack();
    const addToStackAction = resultActionConfig?.displayActions?.find(
      (action) => action.key === "add_to_stack",
    );
    addToStackAction?.onClick?.("click");
  }, [logAddToStack, resultActionConfig]);

  // Only Dropbox files should use the native viewer
  // All other files should use the summary-first variant
  const shouldUseViewer = shouldUseDropboxViewer(result);

  if (!shouldUseViewer) {
    return (
      <SummaryFirstDocumentPreviewModal
        open={open}
        onClose={onClose}
        result={result}
        resultActionConfig={resultActionConfig}
      />
    );
  }

  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      className={styles.modalWindow}
      width="large"
      aria-label={t("document_preview_modal_label")}
    >
      <AnimatePresence>
        {open && (
          <>
            <motion.div {...MODAL_ANIMATIONS.header}>
              <DocumentPreviewHeader
                result={result}
                onClose={onClose}
                onOpenInProvider={handleOpenInProvider}
                onCopyLink={handleCopyLink}
                onAddToStack={handleAddToStack}
                resultActionConfig={resultActionConfig}
              />
            </motion.div>

            <motion.div {...MODAL_ANIMATIONS.body}>
              <Modal.Body className={styles.body}>
                <div
                  className={classNames(styles.contentContainer, {
                    [styles.dragging]: isDragging,
                  })}
                  ref={containerRef}
                  style={
                    {
                      "--preview-width": `${previewWidth}%`,
                      "--details-width": `${100 - previewWidth}%`,
                    } as React.CSSProperties
                  }
                >
                  {isDragging && <div className={styles.dragOverlay} />}

                  <DocumentPreviewArea
                    result={result}
                    currentPage={currentPage}
                    zoomLevel={zoomLevel}
                    onZoomIn={handleZoomIn}
                    onZoomOut={handleZoomOut}
                    onZoomChange={handleZoomChange}
                    onNavigate={logNavigation}
                    onFileDownloadComplete={logFileDownloadComplete}
                  />

                  <button
                    className={classNames(styles.resizeHandle, {
                      [styles.dragging]: isDragging,
                    })}
                    onMouseDown={handleMouseDown}
                    aria-label={t("resize_preview_area_label")}
                  />

                  <DocumentDetailsPanel
                    result={result}
                    showMoreDetails={showMoreDetails}
                    onToggleDetails={() => setShowMoreDetails(!showMoreDetails)}
                    onSuggestionClick={handleSuggestionClick}
                  />
                </div>
              </Modal.Body>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </Modal>
  );
}
